// NEVANU — Kompatibilitäts-Brücke
// Exportiert neue TypeScript-Module als window.* Globals
// damit Legacy-Code (form.js, buchungen.js, etc.) weiter funktioniert.
//
// Diese Datei wird ENTFERNT sobald die Migration abgeschlossen ist.

import { api, apiUpload, apiLegacy } from './core/api-client';
import { getSession, saveSession, clearSession, isLoggedIn, getCurrentUser, getCurrentRole, isAdmin, isManager, getRoleLabel } from './core/auth';
import { escHtml, escAttr, showToast, formatDate, dateToInput, addBottomSheetSwipe } from './core/dom';
import { registerRoute, navigateTo, getCurrentTab } from './core/router';
import { getThemeMode, setThemeMode, cycleThemeMode, applyTheme, initTheme, getBgMode, setBgMode } from './core/theme';

// API Client auf window
(window as any).nevanuApi = api;
(window as any).nevanuApiUpload = apiUpload;
(window as any).nevanuApiLegacy = apiLegacy;

// Auth auf window
(window as any).nevanuAuth = {
  getSession,
  saveSession,
  clearSession,
  isLoggedIn,
  getCurrentUser,
  getCurrentRole,
  isAdmin,
  isManager,
  getRoleLabel,
};

// DOM Utilities auf window (ergänzt bestehende globale Funktionen)
(window as any).nevanuDom = {
  escHtml,
  escAttr,
  showToast,
  formatDate,
  dateToInput,
  addBottomSheetSwipe,
};

// Router auf window
(window as any).nevanuRouter = {
  registerRoute,
  navigateTo,
  getCurrentTab,
};

// Theme auf window
(window as any).nevanuTheme = {
  getThemeMode,
  setThemeMode,
  cycleThemeMode,
  applyTheme,
  initTheme,
  getBgMode,
  setBgMode,
};

// ============================================================
// Feature-Module als Lazy-Loaded Routes registrieren
// ============================================================

// HOME: Deaktiviert — form.js _renderKeevaHome ist jetzt der einzige Home-Renderer
// registerRoute('home', () => import('./features/home/home'));
registerRoute('calculator', () => import('./features/calculator/calculator'));
registerRoute('rechner', () => import('./features/calculator/calculator')); // Alias
// Module die loadXXX statt load exportieren brauchen Wrapper
const wrap = (loader: () => Promise<any>, fnName: string) => async () => {
  const m = await loader();
  return { load: (c: HTMLElement) => m[fnName](c), unload: m.unload };
};
registerRoute('customers', wrap(() => import('./features/customers/customers'), 'loadKunden'));
registerRoute('kunden', wrap(() => import('./features/customers/customers'), 'loadKunden')); // Alias
registerRoute('services', () => import('./features/services/services'));
registerRoute('leistungen', () => import('./features/services/services')); // Alias
registerRoute('tasks', wrap(() => import('./features/tasks/tasks'), 'loadAufgaben'));
registerRoute('aufgaben', wrap(() => import('./features/tasks/tasks'), 'loadAufgaben')); // Alias
registerRoute('news', wrap(() => import('./features/news/news'), 'loadNewsPage'));
registerRoute('nachrichten', wrap(() => import('./features/news/news'), 'loadNewsPage')); // Alias
registerRoute('orders', wrap(() => import('./features/orders/orders'), 'loadBestellungen'));
registerRoute('bestellungen', wrap(() => import('./features/orders/orders'), 'loadBestellungen')); // Alias
registerRoute('team', wrap(() => import('./features/team/team'), 'loadTeamManager'));
registerRoute('inventory', wrap(() => import('./features/inventory/inventory'), 'loadLagerView'));
registerRoute('lager', wrap(() => import('./features/inventory/inventory'), 'loadLagerView')); // Alias
registerRoute('timetracking', () => import('./features/timetracking/timetracking'));
registerRoute('zeiterfassung', () => import('./features/timetracking/timetracking')); // Alias
registerRoute('calendar', () => import('./features/calendar/calendar'));
registerRoute('kalender', () => import('./features/calendar/calendar')); // Alias
registerRoute('consultation-form', () => import('./features/consultation-form/consultation-form'));
registerRoute('beratungsprotokoll', () => import('./features/consultation-form/consultation-form')); // Alias
registerRoute('settings', () => import('./features/settings/settings'));
registerRoute('profil', () => import('./features/settings/settings')); // Profil-Ansicht
registerRoute('neukunden', () => import('./features/neukunden/neukunden'));
registerRoute('projekt', () => import('./features/project/project'));
registerRoute('export', () => import('./features/export/export'));
registerRoute('medien', wrap(() => import('./features/media/media'), 'loadMedien'));

// Legacy-Wrapper für noch nicht migrierte Tabs
// Diese delegieren an die globalen Funktionen aus form.js
const legacyRoute = (fn: string) => () => Promise.resolve({
  load: (c: HTMLElement) => { if (typeof (window as any)[fn] === 'function') (window as any)[fn](c); },
  unload: () => {},
});
// 'einstellungen' wird DIREKT in shell.ts loadDashboard() aufgerufen (Router umgangen)
registerRoute('protokolle', legacyRoute('loadProtokolle'));
registerRoute('kollegen', legacyRoute('loadKollegenView'));
registerRoute('terminliste', legacyRoute('loadTerminliste'));
registerRoute('arbeitszeiten', legacyRoute('loadArbeitszeitenAdmin'));
registerRoute('kampagnen', legacyRoute('loadKampagnen'));
registerRoute('berichte', legacyRoute('loadBerichte'));

// ============================================================
// Globale Module (nicht route-basiert, werden sofort geladen)
// ============================================================

// Notifications + Badge System (global, nicht route-gebunden)
import('./features/notifications/notifications');

// Bug Report System (globale Event Listeners)
import('./features/bugreport/bugreport');

// App Shell (Dashboard, Navigation, Login, Init)
import './shell/shell';

// Marker dass das neue System geladen ist
(window as any).__NEVANU_MODULES_LOADED = true;

console.log('[NEVANU] Module system initialized ✓ (18 feature routes + 3 global modules)');
